/* ============================================================================
 * NEWS_DESK.EXE — Presence station + EAS desk
 * ----------------------------------------------------------------------------
 * PRESENCE and MOMENTS are canon (ported from SupercomputeA/presence):
 *   • 15s rotating QR, deterministic keccak hash-chain of secrets
 *   • three ordered gates: in-chain → grace window → hasAttended (soulbound)
 *   • EIP-712 domain { name: "EAS", version: "1.4.0", chainId: 84532 }
 *   • schema UIDs are ZEROED until Phase A5; canon refuses to invent them
 *
 * DISPATCH and LOOKUP are Supercompute-local extras, clearly marked.
 * ========================================================================= */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useAccount,
  useBlockNumber,
  useChainId,
  useGasPrice,
  useReadContract,
  useWalletClient,
  useWriteContract,
} from 'wagmi';
import { base, baseSepolia } from 'wagmi/chains';
import { formatGwei, type Address, type Hex } from 'viem';
import {
  DISPATCH,
  EAS_ABI,
  PRESENCE_EVENT,
  PRESENCE_MOMENT,
  ZERO_ADDRESS,
  ZERO_BYTES32,
  encodeDispatch,
  encodePresenceEvent,
  encodePresenceMoment,
  easAddress,
} from '../lib/eas';
import {
  GRACE_WINDOWS,
  PresenceStation,
  ROTATION_MS,
  attestDigest,
  decodeTicket,
  encodeTicket,
  relayClaim,
  relayConfigFromEnv,
  signAttest,
  type AttestMessage,
  type ClaimVerdict,
} from '../lib/presence';
import { PRESENCE_CHAIN, isUnverifiedAddress } from '../lib/presence-chain';
import { pinJson } from '../lib/ipfs';
import { QrCode } from '../components/QrCode';
import { NewsDeskBlog } from './NewsDeskBlog';
import { NewsDeskLexicon } from './NewsDeskLexicon';

type Tab = 'presence' | 'blog' | 'lexicon' | 'moments' | 'dispatch' | 'lookup';

interface LogLine {
  id: number;
  kind: 'info' | 'ok' | 'err';
  text: string;
}

function useLog() {
  const [lines, setLines] = useState<LogLine[]>([]);
  const seq = useRef(0);
  const push = useCallback((kind: LogLine['kind'], text: string) => {
    setLines((prev) => [...prev.slice(-70), { id: ++seq.current, kind, text }]);
  }, []);
  return { lines, push };
}

function Log({ lines, placeholder }: { lines: LogLine[]; placeholder: string }) {
  return (
    <div className="log">
      {lines.length === 0 && (
        <div className="log__line log__line--dim">{placeholder}</div>
      )}
      {lines.map((l) => (
        <div
          key={l.id}
          className={
            'log__line' +
            (l.kind === 'err'
              ? ' log__line--err'
              : l.kind === 'ok'
                ? ' log__line--ok'
                : ' log__line--dim')
          }
        >
          {'> '}
          {l.text}
        </div>
      ))}
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <div className="row">
      <span className="row__k">{k}</span>
      <span className={`row__v${mono ? ' mono' : ''}`} style={mono ? { fontSize: 10 } : undefined}>
        {v}
      </span>
    </div>
  );
}

function short(v: string, n = 18): string {
  return v.length > n * 2 ? `${v.slice(0, n)}…${v.slice(-6)}` : v;
}

/* ══ PRESENCE ══════════════════════════════════════════════════════════════ */

function PresenceDesk() {
  const [eventName, setEventName] = useState('Supercompute Embassy 01');
  const [location, setLocation] = useState('Los Angeles, CA');
  const startedAt = useMemo(() => BigInt(Math.floor(Date.now() / 1000)), []);
  const [, setTick] = useState(0);
  const [ticketUri, setTicketUri] = useState('');
  const [verdict, setVerdict] = useState<ClaimVerdict | null>(null);
  const [signature, setSignature] = useState<Hex | null>(null);
  const { lines, push } = useLog();

  const { address: account } = useAccount();
  const { data: signerClient } = useWalletClient();

  const station = useMemo(
    () => new PresenceStation({ eventName, location, timestamp: startedAt }),
    [eventName, location, startedAt],
  );

  useEffect(() => {
    const t = window.setInterval(() => {
      station.sync();
      setTick((n) => n + 1);
    }, 500);
    return () => window.clearInterval(t);
  }, [station]);

  const ticket = station.ticket();
  const uri = encodeTicket(ticket);
  const msLeft = station.msToRotate();
  const secondsLeft = Math.ceil(msLeft / 1000);

  const claimMessage: AttestMessage | null = useMemo(() => {
    if (!account || !verdict?.secretHash) return null;
    return {
      // Can a UID be filled in? No — canon registers schemas in A5.
      schema: ZERO_BYTES32,
      recipient: account,
      expirationTime: 0n,
      revocable: PRESENCE_EVENT.revocable,
      refUID: ZERO_BYTES32,
      data: encodePresenceEvent({
        eventId: station.eventId,
        eventName: station.eventName,
        timestamp: station.timestamp,
        location: station.location,
        secretHash: verdict.secretHash,
      }),
      value: 0n, // resolver isPayable() == false; canon reverts on any value
    };
  }, [account, verdict, station]);

  const doClaim = useCallback(() => {
    const parsed = decodeTicket(ticketUri.trim());
    if (!parsed) {
      push('err', 'Not a valid Presence ticket URI.');
      return;
    }
    if (!account) {
      push('err', 'Connect a wallet — the recipient must be non-zero (soulbound).');
      return;
    }
    const v = station.claim(account, parsed.secret);
    setVerdict(v);
    setSignature(null);
    push(v.ok ? 'ok' : 'err', v.reason);
  }, [ticketUri, account, station, push]);

  const doSign = useCallback(async () => {
    if (!signerClient || !account || !claimMessage) {
      push('err', 'Signer or payload unavailable.');
      return;
    }
    try {
      const sig = await signAttest(signerClient, account, claimMessage);
      setSignature(sig);
      push('ok', `Signature ${sig.slice(0, 22)}… (PREVIEW — schema UID is zeroed until A5)`);

      // Hand it to the gasless relayer. With none configured this returns
      // 'signed' — the claim is valid, it just was not broadcast. Say so.
      const outcome = await relayClaim(
        relayConfigFromEnv(),
        {
          eventId: station.eventId,
          eventName: station.eventName,
          recipient: account,
          windowIndex: verdict?.windowIndex ?? -1,
          secretHash: verdict?.secretHash ?? null,
        },
        sig,
      );
      if (outcome.status === 'relayed') {
        push('ok', `Relayed → ${outcome.attestationUID}`);
      } else if (outcome.status === 'signed') {
        push('err', `Signed but NOT broadcast — ${outcome.reason}`);
      } else {
        push('err', `Relay failed — ${outcome.error}`);
      }
    } catch (e) {
      push('err', e instanceof Error ? e.message : String(e));
    }
  }, [signerClient, account, claimMessage, push, station, verdict]);

  const replayStale = useCallback(() => {
    if (!account) {
      push('err', 'Connect a wallet first.');
      return;
    }
    const staleSecret = station.history[0].secret;
    const v = station.claim(account, staleSecret);
    setVerdict(v);
    push(v.ok ? 'ok' : 'err', v.reason);
  }, [account, station, push]);

  const verdictTag = (code: string) =>
    code === 'OK' ? 'tag--ok' : code === 'ALREADY_ATTENDED' ? 'tag--warn' : 'tag--err';

  return (
    <div className="stack">
      {/* event definition */}
      <div className="hstack">
        <label className="label" htmlFor="pd-name">Event</label>
        <input
          id="pd-name"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', flex: '1 1 auto' }}
        />
      </div>
      <div className="hstack">
        <label className="label" htmlFor="pd-loc">Location</label>
        <input
          id="pd-loc"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', flex: '1 1 auto' }}
        />
      </div>

      <div className="panel">
        <Row k="Schema" v={PRESENCE_EVENT.schema} mono />
        <Row k="canon" v={<span className="tag tag--ok">true</span>} />
        <Row k="revocable" v={String(PRESENCE_EVENT.revocable)} />
        <Row k="resolver" v={<span className="tag tag--warn">{PRESENCE_EVENT.resolverAddress}</span>} />
      </div>

      <div className="hstack" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div className="stack" style={{ alignItems: 'center', gap: 4 }}>
          <QrCode value={uri} alt={`Rotating Presence QR — window ${ticket.windowIndex}`} />
          <span className="note">
            rotation #{ticket.windowIndex} · {secondsLeft}s
          </span>
        </div>
        <div className="stack" style={{ flex: '1 1 auto', gap: 2 }}>
          <span className="eyebrow">rotating secret</span>
          <span className="value" style={{ fontSize: 10 }}>{ticket.secret}</span>
          <span className="eyebrow" style={{ marginTop: 6 }}>secretHash</span>
          <span className="value" style={{ fontSize: 10 }}>{ticket.secretHash}</span>
          <span className="eyebrow" style={{ marginTop: 6 }}>window</span>
          <span className="note">
            {ROTATION_MS / 1000}s · grace {GRACE_WINDOWS} · {station.history.length} windows seen
          </span>
        </div>
      </div>

      <p className="note">
        Secrets are a deterministic hash-chain from one root:{' '}
        <code>keccak256(root ‖ uint32(index))</code>. A claim is only accepted from
        the current window or the previous one — a photographed QR stops working.
      </p>

      <hr className="rule" />

      {/* attendee claim */}
      <span className="eyebrow">attendee — claim</span>
      <textarea
        value={ticketUri}
        onChange={(e) => setTicketUri(e.target.value)}
        rows={3}
        placeholder="Paste the supercompute://presence?d=… URI the station produced"
        style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', resize: 'vertical', fontSize: 10 }}
      />
      <div className="hstack">
        <button type="button" className="btn btn--primary" onClick={doClaim}>
          Claim
        </button>
        <button type="button" className="btn" onClick={() => setTicketUri(uri)}>
          Use current ticket
        </button>
        <button type="button" className="btn" onClick={() => station.forceRotate()}>
          Force rotation
        </button>
        <button type="button" className="btn" onClick={replayStale}>
          Replay window #0 (stale)
        </button>
      </div>

      {verdict && (
        <div className="panel stack" style={{ gap: 4 }}>
          <span className={`tag ${verdictTag(verdict.code)}`}>{verdict.code}</span>
          <span className="note">{verdict.reason}</span>
        </div>
      )}

      <Log lines={lines} placeholder="ready. claims are gasless — no transaction." />

      {/* EIP-712 payload */}
      <span className="eyebrow">eip-712 attest payload</span>
      <div className="panel">
        <Row k="Domain" v="EAS · 1.4.0 · 84532" />
        <Row k="verifyingContract" v={<span className="tag tag--warn">omitted (canon TBD)</span>} />
        <Row k="primaryType" v="Attest" />
        <Row k="schema" v={ZERO_BYTES32} mono />
        <Row k="recipient" v={claimMessage?.recipient ?? <span className="tag tag--muted">claim first</span>} mono />
        <Row k="value" v="0 — resolver isPayable() == false" />
        <Row
          k="digest"
          v={claimMessage ? attestDigest(claimMessage) : <span className="tag tag--muted">—</span>}
          mono
        />
      </div>
      <div className="hstack">
        <button
          type="button"
          className="btn"
          onClick={doSign}
          disabled={!claimMessage || !signerClient}
        >
          Sign (organiser)
        </button>
        {signature && <span className="tag tag--ok">SIGNED · PREVIEW</span>}
      </div>
      <p className="note note--warn">
        The schema UID is zeroed because canon registers PresenceEvent in Phase A5.
        A signature over a zeroed UID is a faithful preview of the A2 payload, not
        a submittable attestation.
      </p>
    </div>
  );
}

/* ══ MOMENTS ═══════════════════════════════════════════════════════════════ */

function MomentsDesk() {
  const [mediaCid, setMediaCid] = useState('');
  const [mediaType, setMediaType] = useState('image/jpeg');
  const [caption, setCaption] = useState('');
  const [parentUid, setParentUid] = useState<Hex>(ZERO_BYTES32);
  const [pinned, setPinned] = useState<string | null>(null);
  const { lines, push } = useLog();

  const capturedAt = useMemo(() => BigInt(Math.floor(Date.now() / 1000)), []);

  const encoded = useMemo(
    () => encodePresenceMoment({ mediaCid, mediaType, capturedAt, caption }),
    [mediaCid, mediaType, capturedAt, caption],
  );

  const doPin = useCallback(async () => {
    const res = await pinJson(
      { v: 1, mediaType, caption, capturedAt: capturedAt.toString() },
      { name: `moment-${mediaType}` },
    );
    setPinned(res.uri);
    if (res.pinned && res.cid) {
      setMediaCid(res.cid);
      push('ok', `Pinned → ipfs://${res.cid}`);
    } else {
      push('err', `NOT PINNED — ${res.reason}`);
    }
  }, [mediaType, caption, capturedAt, push]);

  return (
    <div className="stack">
      <span className="eyebrow">PresenceMoment — canon schema</span>
      <div className="panel">
        <Row k="Schema" v={PRESENCE_MOMENT.schema} mono />
        <Row k="revocable" v={String(PRESENCE_MOMENT.revocable)} />
        <Row k="resolver" v={<span className="tag tag--warn">{PRESENCE_MOMENT.resolverAddress}</span>} />
      </div>
      <p className="note">
        The link to the parent event is an <strong>attestation-level refUID</strong>,
        not a schema field — the schema deliberately has no eventUid. Moments are
        how photos, voice and journal entries become self-custodied memories.
      </p>

      <div className="stack" style={{ gap: 4 }}>
        <label className="label" htmlFor="m-cid">mediaCid</label>
        <input
          id="m-cid"
          value={mediaCid}
          onChange={(e) => setMediaCid(e.target.value)}
          placeholder="bafybei…"
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', fontSize: 10 }}
        />
        <label className="label" htmlFor="m-type">mediaType</label>
        <input
          id="m-type"
          value={mediaType}
          onChange={(e) => setMediaType(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px' }}
        />
        <label className="label" htmlFor="m-cap">caption</label>
        <input
          id="m-cap"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px' }}
        />
        <label className="label" htmlFor="m-ref">parent event attestation UID (refUID)</label>
        <input
          id="m-ref"
          value={parentUid}
          onChange={(e) => setParentUid(e.target.value as Hex)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', fontSize: 10 }}
        />
      </div>

      <div className="hstack">
        <button type="button" className="btn" onClick={doPin}>
          Pin media
        </button>
        {pinned && <span className="tag tag--ok">PINNED</span>}
      </div>

      <div className="panel">
        <Row k="capturedAt" v={`${capturedAt} · ${new Date(Number(capturedAt) * 1000).toISOString()}`} />
        <Row k="abi.encode" v={short(encoded, 22)} mono />
        <Row k="refUID" v={parentUid === ZERO_BYTES32 ? 'zero — root (no parent yet)' : parentUid} mono />
      </div>

      <Log lines={lines} placeholder="ready. moments attest only after the media is pinned." />
    </div>
  );
}

/* ══ DISPATCH (Supercompute-local) ═════════════════════════════════════════ */

function DispatchDesk({ signer }: { signer: Address | undefined }) {
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('supercompute, dispatch');
  const { lines, push } = useLog();
  const { writeContractAsync } = useWriteContract();
  const chainId = useChainId();

  const submit = useCallback(async () => {
    if (!signer) {
      push('err', 'Abort: connect a wallet first.');
      return;
    }
    if (!headline.trim()) {
      push('err', 'Abort: headline is required.');
      return;
    }
    try {
      push('info', 'Pinning dispatch body to IPFS…');
      const pinned = await pinJson(
        { v: 1, headline, body, publishedAt: new Date().toISOString(), author: signer },
        { name: `dispatch-${headline}` },
      );
      if (!pinned.pinned) {
        push('err', `NOT PINNED — ${pinned.reason}`);
        push('err', 'Refusing to attest an unpinned body.');
        return;
      }
      push('ok', `Pinned → ${pinned.uri}`);
      const encoded = encodeDispatch({
        headline,
        bodyURI: pinned.uri,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        publishedAt: BigInt(Math.floor(Date.now() / 1000)),
        authorENS: 'unresolved.eth',
      });
      push('info', 'Requesting signature (attest)…');
      const hash = await writeContractAsync({
        address: easAddress(chainId),
        abi: EAS_ABI,
        functionName: 'attest',
        args: [
          {
            schema: ZERO_BYTES32,
            data: {
              recipient: ZERO_ADDRESS,
              expirationTime: 0n,
              revocable: DISPATCH.revocable,
              refUID: ZERO_BYTES32,
              data: encoded,
              value: 0n,
            },
          },
        ],
      });
      push('ok', `Broadcast → ${hash}`);
    } catch (e) {
      push('err', e instanceof Error ? e.message : String(e));
    }
  }, [signer, headline, body, tags, push, writeContractAsync, chainId]);

  return (
    <div className="stack">
      <span className="eyebrow">Supercompute-local schema — not canon</span>
      <div className="panel">
        <Row k="Schema" v={DISPATCH.schema} mono />
        <Row k="canon" v={<span className="tag tag--warn">false</span>} />
      </div>

      <div className="stack" style={{ gap: 4 }}>
        <label className="label" htmlFor="nd-headline">Headline</label>
        <input
          id="nd-headline"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px' }}
        />
        <label className="label" htmlFor="nd-body">Body</label>
        <textarea
          id="nd-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', resize: 'vertical' }}
        />
        <label className="label" htmlFor="nd-tags">Tags (comma separated)</label>
        <input
          id="nd-tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px' }}
        />
      </div>

      <div className="hstack">
        <button type="button" className="btn btn--primary" onClick={submit}>
          Pin + dispatch
        </button>
      </div>

      <Log lines={lines} placeholder="ready. body is pinned before any attestation is written." />
    </div>
  );
}

/* ══ LOOKUP ════════════════════════════════════════════════════════════════ */

function LookupDesk() {
  const [input, setInput] = useState('');
  const [uid, setUid] = useState<Hex | undefined>();
  const chainId = useChainId();

  const { data, isLoading, error } = useReadContract({
    address: easAddress(chainId),
    abi: EAS_ABI,
    functionName: 'getAttestation',
    args: uid ? [uid] : undefined,
    query: { enabled: Boolean(uid) },
  });

  const exists = Boolean(data && data.uid !== ZERO_BYTES32);

  return (
    <div className="stack">
      <div className="hstack">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="0x… attestation UID"
          style={{ background: 'var(--site-bg)', border: '1px solid var(--wos-border-dim)', padding: '4px 6px', flex: '1 1 auto', fontSize: 11 }}
        />
        <button
          type="button"
          className="btn"
          onClick={() => setUid(input.trim() as Hex)}
          disabled={!/^0x[0-9a-fA-F]{64}$/.test(input.trim())}
        >
          Read
        </button>
      </div>

      {isLoading && <p className="note">reading registry…</p>}
      {error && <p className="note note--warn">{error.message}</p>}
      {uid && !isLoading && !exists && (
        <p className="note note--warn">
          No attestation at that UID on chain {chainId}. (Unregistered UIDs decode to zero.)
        </p>
      )}

      {exists && data && (
        <div className="panel">
          <Row k="UID" v={data.uid} mono />
          <Row k="Schema" v={data.schema} mono />
          <Row k="Attester" v={data.attester} mono />
          <Row k="Recipient" v={data.recipient} mono />
          <Row k="refUID" v={data.refUID} mono />
          <Row k="Time" v={new Date(Number(data.time) * 1000).toISOString()} />
          <Row k="Revoked" v={data.revocationTime > 0n ? new Date(Number(data.revocationTime) * 1000).toISOString() : 'no'} />
          <Row k="Payload" v={data.data} mono />
        </div>
      )}

      <p className="note">
        Reads against chain {chainId}. Presence lives on Base Sepolia (84532); the
        resolver address is canon&apos;s literal{' '}
        <code>{PRESENCE_CHAIN.resolverAddress}</code>
        {isUnverifiedAddress(PRESENCE_CHAIN.resolverAddress) ? ' — not yet deployed.' : '.'}
      </p>
    </div>
  );
}

/* ══ Shell ═════════════════════════════════════════════════════════════════ */

/**
 * THE NAMEPLATE. A news desk without a masthead is a tab strip with a header —
 * which is what this desk used to be. A nameplate is five stacked bands with
 * fixed jobs (edition rule · edition line · the plate · the dateline · the
 * section bar), and it is the one place the paper speaks in its own voice: the
 * display face at 40px over a dateline that is a machine readout — block,
 * chain, gas — because those are the facts a reader of THIS paper needs in
 * order to trust the copy.
 *
 * The dateline is read live. If a read fails it says `unread`; a stale block
 * presented as current is the one lie a dateline cannot tell.
 */
const NEWS_TABS = [
  { id: 'presence', label: 'Presence' },
  { id: 'blog', label: 'Blog' },
  { id: 'lexicon', label: 'Lexicon' },
  { id: 'moments', label: 'Moments' },
  { id: 'dispatch', label: 'Dispatch' },
  { id: 'lookup', label: 'Lookup' },
] as const;

export function NewsDesk() {
  const [tab, setTab] = useState<Tab>('presence');
  const { address } = useAccount();

  /* the dateline — two live reads against Base mainnet */
  const { data: blockNumber } = useBlockNumber({ chainId: base.id, watch: true });
  const { data: gasPrice } = useGasPrice({ chainId: base.id });

  const dateline = new Date().toUTCString().replace(',', '').slice(0, 16);
  const gas = gasPrice !== undefined ? `${Number(formatGwei(gasPrice)).toFixed(3)}` : null;

  const resolverState = isUnverifiedAddress(PRESENCE_CHAIN.resolverAddress);

  return (
    <div className="dsp page">
      <div className="nameplate" data-od-id="newsdesk-nameplate">
        <div className="np__top">
          <span>Est. 2026</span>
          <span>Sovereign publishing</span>
          <span className="r">Vol. 01 · No. 001</span>
        </div>

        <div className="np__plate">
          <img
            src="/logos/supercompute-mark-64.png"
            width={34}
            height={34}
            alt=""
            aria-hidden="true"
          />
          <span className="np__word">Supercompute</span>
        </div>

        <div className="np__rule" />

        <div className="np__date">
          <span className="live">
            <span className="dot" aria-hidden="true" />
            {blockNumber ? (
              `Block ${blockNumber.toLocaleString('en-US')}`
            ) : (
              <>
                Block <span className="unread">unread</span>
              </>
            )}
          </span>
          <span>
            Base <b>8453</b>
          </span>
          <span>
            Gas{' '}
            <b>
              {gas ?? <span className="unread">unread</span>}
              {gas ? ' gwei' : null}
            </b>
          </span>
          <span>{dateline} UTC</span>
        </div>

        <div className="sections" role="tablist" aria-label="News desk modules">
          {NEWS_TABS.map((t) => (
            <button
              key={t.id}
              className="stab"
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* standing matter: the facts that are true of every module below */}
      <div className="page__bar">
        <div className="grow">
          <span className="eyebrow">this desk — standing matter</span>
          <span className="micro">
            The four facts that hold whichever section you are reading.
          </span>
        </div>
      </div>

      <div className="panel">
        <Row k="Chain" v={`${PRESENCE_CHAIN.chainName} (${PRESENCE_CHAIN.chainId})`} />
        <Row k="EAS" v={easAddress(baseSepolia.id)} mono />
        <Row
          k="Resolver"
          v={
            <span className="tag tag--warn">
              {PRESENCE_CHAIN.resolverAddress}
              {resolverState ? ' — not yet deployed' : ''}
            </span>
          }
        />
        <Row k="Signer" v={address ?? <span className="tag tag--warn">wallet not connected</span>} />
      </div>

      {tab === 'presence' && <PresenceDesk />}
      {tab === 'blog' && <NewsDeskBlog />}
      {tab === 'lexicon' && <NewsDeskLexicon />}
      {tab === 'moments' && <MomentsDesk />}
      {tab === 'dispatch' && <DispatchDesk signer={address} />}
      {tab === 'lookup' && <LookupDesk />}

      <div className="page__status" data-od-id="newsdesk-provenance">
        <span>
          edition <b>vol. 01 · no. 001</b>
        </span>
        <span>
          reading <b>{NEWS_TABS.find((t) => t.id === tab)?.label}</b>
        </span>
        <span>
          dateline{' '}
          <b className={blockNumber ? '' : 'unread'}>
            {blockNumber ? blockNumber.toLocaleString('en-US') : 'unread'}
          </b>
        </span>
        <span>
          publishing <b>write it · split it · prove it</b>
        </span>
      </div>
    </div>
  );
}

